/**
 * Pure weapon-card formatters shared by every weapon-card render surface: the Skirmish/Barrage
 * choosers, the GM's NPC control panel, and the player's NPC dossier. Kept out of the components so
 * they're unit-testable without a renderer, matching the sibling pure modules (`disclosure.ts`,
 * `npcFeatures.ts`, `combatInspector.ts`).
 */
import type { WeaponDamage, WeaponProfile, WeaponRange } from "@lancer-vtt/domain";

/** Short damage-type tag for a weapon card's damage line. Unknown types fall back to upper-cased. */
const DMG_ABBR: Record<string, string> = {
  kinetic: "KIN",
  energy: "NRG",
  explosive: "EXP",
  burn: "BRN",
  heat: "HEAT",
  variable: "VAR",
};

/** Render a frozen {@link WeaponDamage} as a compact card line, e.g. `2d6+1 KIN` / `3 EXP` / `— KIN`. */
export function formatWeaponDamage(d: WeaponDamage): string {
  const type = DMG_ABBR[d.type] ?? d.type.toUpperCase();
  const dice = d.count > 0 ? `${d.count}d${d.sides}` : "";
  const bonus = d.bonus ? (dice ? `+${d.bonus}` : `${d.bonus}`) : "";
  const num = `${dice}${bonus}` || "—";
  return `${num} ${type}`;
}

/** Format frozen structured {@link WeaponRange} entries into a card line, e.g. `Range 10, Blast 1`. */
export function formatWeaponRange(range: WeaponRange[]): string {
  return range.map((r) => `${r.kind.charAt(0).toUpperCase()}${r.kind.slice(1)} ${r.val}`).join(", ");
}

/** Format a frozen tag ref as a chip label — strip the `tg_` prefix, upper-case, append the `{VAL}`. */
export function formatTagChip(id: string, val?: number): string {
  const label = id.replace(/^tg_/, "").replace(/_/g, " ").toUpperCase();
  return val != null ? `${label} ${val}` : label;
}

/**
 * The chips for a weapon card — its frozen tag refs formatted (Accurate {VAL}, AP, Overkill, …) when
 * present, else the flattened `ap`/`overkill`/`reliable` flags as a fallback for a tagless frozen
 * profile. Range and effect are *not* chips: the frozen structured `WeaponProfile.range` drives the
 * canvas reach/AoE wash (`attackRange.ts`) AND is formatted into its own card line (see {@link
 * formatWeaponRange}); a frozen NPC weapon's `effect` prose renders as a card line too. Display
 * only; the resolver never reads range/effect.
 */
export function weaponChips(w: WeaponProfile): string[] {
  if (w.tags && w.tags.length) return w.tags.map((t) => formatTagChip(t.id, t.val));
  const chips: string[] = [];
  if (w.ap) chips.push("AP");
  if (w.overkill) chips.push("OVERKILL");
  if (w.reliable > 0) chips.push(`RELIABLE ${w.reliable}`);
  return chips;
}
